import React from "react";
import {
    BooleanField,
    BooleanInput,
    Create,
    Datagrid,
    DateField,
    DateInput,
    Edit,
    EmailField,
    List,
    PasswordInput,
    required,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
} from "react-admin";
import CustomToolbar from "../CustomToolbar";
import { flexstart, mydiv, myspan, spacebetween } from "../layout";
import { CreateUserDto, UpdateUserDto, Users } from "../types/_generate/Api";
import AvatarField from "./AvatarField";
import MyPagination from "./MyPagination";
import UIDNotFoundWarning from "./UIDNotFoundWarning";
import { UsersFilterSidebar } from "./UsersFilterSidebar";

// ── Service constants ──────────────────────────────────────────────────────────

// Canonical service identifiers shared across backend, admin, and app.
// Do not change existing keys after launch, because they are persisted in userData.services.
const SERVICE_KEYS = [
  "billing",
  "wordpress",
  "wedding",
  "story",
  "sharers",
  "schedule",
  "notification",
  "dnd_form",
  "stock_analysis",
] as const;

type ServiceKey = typeof SERVICE_KEYS[number];

/**
 * Suggested defaults per service.
 * Icons use Material Icons names (Quasar default icon set).
 * Override per-user in the form if needed.
 *
 * category values must match ServiceCategory enum in service.type.ts:
 *   overview | website | content | scheduling | tools
 */
const SERVICE_DEFAULTS: Record<
  ServiceKey,
  { name: string; icon: string; category: string }
> = {
  // name uses i18n key format so Quasar app can localize with t(service.name)
  billing:        { name: "services.billing",        icon: "receipt_long",      category: "overview"    },
  wordpress:      { name: "services.wordpress",      icon: "web",               category: "website"     },
  wedding:        { name: "services.wedding",        icon: "favorite",          category: "website"     },
  story:          { name: "services.story",          icon: "auto_stories",      category: "content"     },
  sharers:        { name: "services.sharers",        icon: "people",            category: "content"     },
  schedule:       { name: "services.schedule",       icon: "calendar_today",    category: "scheduling"  },
  notification:   { name: "services.notification",   icon: "notifications",     category: "scheduling"  },
  dnd_form:       { name: "services.dnd_form",       icon: "drag_indicator",    category: "tools"       },
  stock_analysis: { name: "services.stock_analysis", icon: "candlestick_chart", category: "tools"       },
};

const CATEGORY_CHOICES = [
  { id: "overview",   name: "Overview (總覽)" },
  { id: "website",    name: "Website (網站服務)" },
  { id: "content",    name: "Content (內容分享)" },
  { id: "scheduling", name: "Scheduling (行程通知)" },
  { id: "tools",      name: "Tools (工具)" },
];

const PROTECTED_ADMIN_UIDS = [
  "admin@sallyer.com",
  "admin-develop@sallyer.com",
];

/** Renders name / icon / category inputs for a single service */
const ServiceItemInputs: React.FC<{ serviceKey: ServiceKey }> = ({
  serviceKey,
}) => (
  <div style={{ width: "100%", paddingLeft: "1em", borderLeft: "3px solid #eee", marginBottom: "0.5em" }}>
    <span style={{ fontWeight: 600, fontSize: "0.85em", textTransform: "uppercase", color: "#666" }}>
      {serviceKey.replace(/_/g, " ")}
    </span>
    {/* Row 1: toggle switches */}
    <div style={{ display: "flex", gap: "2em", alignItems: "center", marginBottom: "-0.5em" }}>
      <BooleanInput
        label="Enabled"
        source={`userData.services.${serviceKey}.enabled`}
        defaultValue={false}
      />
      {/* locked=true → visible in sidebar/dashboard but not clickable, until Enabled is turned on */}
      <BooleanInput
        label="Locked"
        source={`userData.services.${serviceKey}.locked`}
        defaultValue={false}
      />
    </div>
    {/* Row 2: text / select fields */}
    <div style={{ display: "flex", gap: "1em", flexWrap: "wrap" }}>
      <TextInput
        label="Name"
        source={`userData.services.${serviceKey}.name`}
        helperText="i18n key (example: services.schedule)"
      />
      <TextInput
        label="Icon"
        source={`userData.services.${serviceKey}.icon`}
        helperText="Material icon name or image URL"
      />
      <SelectInput
        label="Category"
        source={`userData.services.${serviceKey}.category`}
        choices={CATEGORY_CHOICES}
        helperText="Sidebar grouping"
      />
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────────

/** Empty ServiceItem shape used as a fallback default */
/** Default values per service — pre-fills form fields for new/unconfigured services */
const SERVICES_DEFAULT_VALUES = SERVICE_KEYS.reduce(
  (acc, key) => ({
    ...acc,
    [key]: {
      enabled: false,
      locked: false,
      name: SERVICE_DEFAULTS[key].name,
      icon: SERVICE_DEFAULTS[key].icon,
      category: SERVICE_DEFAULTS[key].category,
    },
  }),
  {} as Record<ServiceKey, { enabled: boolean; locked: boolean; name: string; icon: string; category: string }>,
);

export const UserList: React.FC<Users> = () => (
  <List aside={<UsersFilterSidebar />} pagination={<MyPagination />}>
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField label="uid" source="id" />
      <EmailField source="email" />
      <BooleanField source="emailVerified" />
      <BooleanField source="disabled" />
      <BooleanField label="authUser" source="isAuthUser" />
      <TextField label="name" source="displayName" />
      <DateField label="lastSignInTime" source="lastSignInTime" showTime />
      <DateField label="creationTime" source="creationTime" showTime />
    </Datagrid>
  </List>
);

export const UserCreate: React.FC<CreateUserDto> = () => (
  <Create>
    <SimpleForm>
      <TextInput source="email" validate={[required()]} fullWidth />
      <PasswordInput source="password" validate={[required()]} fullWidth />
      <TextInput label="Name" source="displayName" fullWidth />
      <AvatarField label="avatar" source="photoURL" />
      <TextInput label="Photo URL" source="photoURL" fullWidth />
      <BooleanInput
        label="Email Verified"
        source="emailVerified"
        defaultChecked={false}
      />
      <BooleanInput label="Disabled" source="disabled" defaultChecked={false} />
    </SimpleForm>
  </Create>
);

export const UserEdit: React.FC<UpdateUserDto> = () => (
  <Edit redirect="false">
    <SimpleForm
      toolbar={<CustomToolbar protectedIds={PROTECTED_ADMIN_UIDS} />}
      defaultValues={{ userData: { services: SERVICES_DEFAULT_VALUES } }}
    >
      <div style={mydiv}>
        <span style={myspan}>Firebase Authentication</span>
      </div>
      <TextInput disabled label="uid" source="id" fullWidth />
      <TextInput disabled source="email" validate={[required()]} fullWidth />
      <TextInput disabled label="Name" source="displayName" fullWidth />
      <AvatarField disabled source="photoURL" />
      <TextInput disabled label="Photo URL" source="photoURL" fullWidth />
      <div style={flexstart}>
        <BooleanInput label="Email Verified" source="emailVerified" />
        <BooleanInput label="Disabled" source="disabled" />
      </div>
      <div style={mydiv}>
        <span style={myspan}>Users Collection</span>
      </div>
      <UIDNotFoundWarning source="userData.id" />
      <TextInput disabled label="UID" source="userData.id" fullWidth />
      <div style={flexstart}>
        <SelectInput
          label="Role"
          source="userData.role"
          choices={[
            { id: "user", name: "user", value: "user" },
            { id: "admin", name: "admin", value: "admin" },
          ]}
          validate={required()}
        />
        <div style={{ marginBottom: "1em" }}>
          <AvatarField source="userData.profile.avatar" />
        </div>
      </div>
      <TextInput label="Avatar" source="userData.profile.avatar" fullWidth />
      <TextInput label="Name" source="userData.profile.name" fullWidth />
      <div style={spacebetween}>
        <TextInput
          label="Firstname"
          source="userData.profile.firstname"
          fullWidth
        />
        <TextInput
          label="Lastname"
          source="userData.profile.lastname"
          fullWidth
        />
      </div>
      <div style={flexstart}>
        <TextInput label="Phone" source="userData.profile.phone" />
        <DateInput
          label="Birthday"
          source="userData.profile.birthday"
        ></DateInput>
        <TextInput label="Zipcode" source="userData.profile.zipcode" />
      </div>
      <TextInput label="Address" source="userData.profile.address" fullWidth />
      {/* <TextInput label="Country" source="userData.profile.country" fullWidth /> */}
      <TextInput label="Website" source="userData.profile.website" fullWidth />
      <div style={spacebetween}>
        <TextInput
          label="Facebook"
          source="userData.profile.social_media.facebook"
          fullWidth
        />
        <TextInput
          label="Twitter"
          source="userData.profile.social_media.twitter"
          fullWidth
        />
      </div>
      <div style={spacebetween}>
        <TextInput
          label="Instagram"
          source="userData.profile.social_media.instagram"
          fullWidth
        />
        <TextInput
          label="Linkedin"
          source="userData.profile.social_media.linkedin"
          fullWidth
        />
      </div>
      {/* <TextInput
        label="Certificates"
        source="userData.profile.certificates"
        fullWidth
      /> */}

      {/* <ArrayInput label="Skills" source="userData.profile.skills">
        <SimpleFormIterator inline>
          <TextInput source="" label="Tag ID" fullWidth />
          <TextInput source="text" />
          <TextInput source="description" />
          <TextInput source="style" fullWidth />
          <TextInput source="image_url" />
          <AvatarField label="avatar" source="image_url" />
        </SimpleFormIterator>
      </ArrayInput> */}
      <TextInput
        label="Education"
        source="userData.profile.education"
        multiline
        fullWidth
      />
      <TextInput
        label="Experience"
        source="userData.profile.experience"
        multiline
        fullWidth
      />
      <TextInput
        label="Note"
        source="userData.profile.note"
        multiline
        fullWidth
      />
      <div style={flexstart}>
        <BooleanInput label="Onboarding" source="userData.onboarding" />
        <BooleanInput
          label="Profile Published"
          source="userData.profile_published"
        />
        <BooleanInput
          label="Profile Approved"
          source="userData.profile_approved"
        />
      </div>
      <div style={mydiv}>
        <span style={myspan}>Services</span>
      </div>
      {SERVICE_KEYS.map((key) => (
        <ServiceItemInputs key={key} serviceKey={key} />
      ))}
    </SimpleForm>
  </Edit>
);
